<template>
  <div>
    <point-toolbar :point-info="point.name" />
    <v-container>
      <v-card>
        <v-card-title>Векторная диаграмма полной мощности</v-card-title>
        <v-card-text v-if="phaseDataLoading">
          <v-layout
            justify-center
            align-center
            class="loading-indicator"
          >
            <v-progress-circular
              indeterminate
              :size="70"
              :width="7"
            />
          </v-layout>
        </v-card-text>
        <v-card-text
          v-if="!phaseDataLoading && hasPhaseData"
          class="d-flex flex-row"
        >
          <v-data-table
            :headers="phaseDataHeaders"
            :items="phaseData"
            :items-per-page="50"
            hide-default-footer
            no-data-text="Нет данных для отображения"
            class="pa-2 phase-table"
            dense
          />
          <span class="pa-6">
            <svg
              width="400"
              height="280"
            >
              <line
                stroke-width="15"
                x1="30"
                y1="30"
                x2="15"
                y2="30"
                stroke="orange"
              />
              <text
                x="40"
                y="35"
                class="small"
              >Фаза A</text>
              <line
                stroke-width="15"
                x1="30"
                y1="50"
                x2="15"
                y2="50"
                stroke="green"
              />
              <text
                x="40"
                y="55"
                class="small"
              >Фаза B</text>
              <line
                stroke-width="15"
                x1="30"
                y1="70"
                x2="15"
                y2="70"
                stroke="blue"
              />
              <text
                x="40"
                y="75"
                class="small"
              >Фаза C</text>
              <defs>
                <marker
                  id="arrow_orange"
                  viewBox="0 0 20 20"
                  refX="5"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path
                    d="M 0 0 L 10 5 L 0 10 z"
                    fill="orange"
                  />
                </marker>
                <marker
                  id="arrow_green"
                  viewBox="0 0 20 20"
                  refX="5"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path
                    d="M 0 0 L 10 5 L 0 10 z"
                    fill="green"
                  />
                </marker>
                <marker
                  id="arrow_blue"
                  viewBox="0 0 20 20"
                  refX="5"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path
                    d="M 0 0 L 10 5 L 0 10 z"
                    fill="blue"
                  />
                </marker>
                <marker
                  id="arrow_black"
                  viewBox="0 0 20 20"
                  refX="5"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path
                    d="M 0 0 L 10 5 L 0 10 z"
                    fill="black"
                  />
                </marker>
              </defs>
              <text
                x="335"
                y="142"
                class="small"
              >90&#176;</text>
              <text
                x="200"
                y="275"
                class="small"
              >180&#176;</text>
              <text
                x="55"
                y="145"
                class="small"
              >270&#176;</text>
              <text
                x="205"
                y="15"
                class="small"
              >0&#176;</text>
              <circle
                stroke-width="2"
                stroke="black"
                cx="210"
                cy="140"
                r="120"
                style="fill: white;"
              />
              <line
                stroke-width="1"
                x1="90"
                y1="140"
                x2="330"
                y2="140"
                stroke="black"
              />
              <line
                stroke-width="1"
                x1="210"
                y1="20"
                x2="210"
                y2="260"
                stroke="black"
              />
              <text
                x="290"
                y="15"
                class="heavy"
              >I</text>
              <line
                stroke-width="3"
                x1="290"
                y1="20"
                x2="330"
                y2="20"
                stroke="black"
                marker-end="url(#arrow_black)"
              />
              <text
                x="290"
                y="40"
                class="heavy"
              >U</text>
              <line
                stroke-width="3"
                x1="290"
                y1="45"
                x2="380"
                y2="45"
                stroke="black"
                marker-end="url(#arrow_black)"
              />
              <g
                v-if="showLines"
                class="phase-line"
              >
                <path
                  class="phase-one"
                  :d="'M210 ' + uOne + 'V140'"
                  marker-start="url(#arrow_orange)"
                />
              </g>
              <g
                v-if="showLines"
                class="phase-line"
                :style="{ transform: 'rotate(' + angAdditionalOne + 'deg)' }"
              >
                <path
                  class="phase-one"
                  :d="'M210 ' + iOne + 'V140'"
                  marker-start="url(#arrow_orange)"
                />
              </g>
              <g
                v-if="showLines"
                class="phase-line"
                :style="{ transform: 'rotate(' + angTwo + 'deg)' }"
              >
                <path
                  class="phase-two"
                  :d="'M210 ' + uTwo + 'V140'"
                  marker-start="url(#arrow_green)"
                />
              </g>
              <g
                v-if="showLines"
                class="phase-line"
                :style="{ transform: 'rotate(' + angAdditionalTwo + 'deg)' }"
              >
                <path
                  class="phase-two"
                  :d="'M210 ' + iTwo + 'V140'"
                  marker-start="url(#arrow_green)"
                />
              </g>
              <g
                v-if="showLines"
                class="phase-line"
                :style="{ transform: 'rotate(' + angThree + 'deg)' }"
              >
                <path
                  class="phase-three"
                  :d="'M210 ' + uThree + 'V140'"
                  marker-start="url(#arrow_blue)"
                />
              </g>
              <g
                v-if="showLines"
                class="phase-line"
                :style="{ transform: 'rotate(' + angAdditionalThree + 'deg)' }"
              >
                <path
                  class="phase-three"
                  :d="'M210 ' + iThree + 'V140'"
                  marker-start="url(#arrow_blue)"
                />
              </g>
            </svg>
          </span>
        </v-card-text>
        <v-card-text
          v-if="!phaseDataLoading && !hasPhaseData"
          class="d-flex flex-row"
        >
          Нет данных по фазам счётчика.
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import PointToolbar from '../points/PointToolbar'
import axios from 'axios'

export default {
  name: 'PointConsumption',
  components: {
    PointToolbar
  },
  data () {
    return {
      pointId: this.$route.params['id'],
      point: { name: '' },
      phaseDataHeaders: [
        {
          text: 'Наименование',
          align: 'left',
          sortable: false,
          value: 'name'
        },
        {
          text: 'Фаза A',
          align: 'center',
          sortable: false,
          value: 'v1'
        },
        {
          text: 'Фаза B',
          align: 'center',
          sortable: false,
          value: 'v2'
        },
        {
          text: 'Фаза C',
          align: 'center',
          sortable: false,
          value: 'v3'
        },
        {
          text: 'Сумма',
          align: 'center',
          sortable: false,
          value: 'v4'
        },
        {
          text: '',
          align: 'center',
          sortable: false,
          value: 'measure'
        }
      ],
      phaseData: [],
      additionalAng: 15,
      uOne: 0,
      iOne: 0,
      uTwo: 0,
      iTwo: 0,
      angTwo: 0,
      angAdditionalOne: 0,
      angAdditionalTwo: 0,
      uThree: 0,
      iThree: 0,
      angThree: 0,
      angAdditionalThree: 0,
      showLines: false,
      phaseDataLoading: true,
      hasPhaseData: false
    }
  },
  watch: {},
  mounted () {
    this.getPointApi()
  },
  methods: {
    getPointApi () {
      axios.get(`/api/points/${this.pointId}/`)
        .then((response) => {
          this.point = response.data
          if (this.point.counter_id) {
            this.getCounterPhaseData()
          } else {
            this.phaseDataLoading = false
          }
        })
        .catch((err) => {
          console.log(err)
        })
    },
    getCounterPhaseData () {
      axios.get(`/api/counters/${this.point.counter_id}/phase/`)
        .then((response) => {
          this.hasPhaseData = response.data.values_date != null
          this.phaseData = response.data.values
          var u1 = parseFloat(this.phaseData[4].v1)
          var u2 = parseFloat(this.phaseData[4].v2)
          var u3 = parseFloat(this.phaseData[4].v3)
          var i1 = parseFloat(this.phaseData[5].v1)
          var i2 = parseFloat(this.phaseData[5].v2)
          var i3 = parseFloat(this.phaseData[5].v3)
          this.angTwo = parseFloat(this.phaseData[6].v4)
          this.angThree = parseFloat(this.phaseData[6].v4) + parseFloat(this.phaseData[8].v4)

          var angAddOne = this.phaseData[9].v4 !== '' ? parseFloat(parseFloat(this.phaseData[9].v4)) : this.additionalAng
          var angAddTwo = this.phaseData[10].v4 !== '' ? parseFloat(parseFloat(this.phaseData[10].v4)) : this.additionalAng
          var angAddThree = this.phaseData[11].v4 !== '' ? parseFloat(parseFloat(this.phaseData[11].v4)) : this.additionalAng

          var uMax = Math.max(u1, u2, u3, 0.001)
          var iMax = Math.max(i1, i2, i3, 0.001)
          var lineLen = 140
          var shortLineLen = 70
          var maxLineLen = 165
          this.uOne = maxLineLen - (lineLen / uMax) * u1
          this.iOne = lineLen - shortLineLen / iMax * i1
          this.uTwo = maxLineLen - (lineLen / uMax) * u2
          this.iTwo = lineLen - shortLineLen / iMax * i2
          this.uThree = maxLineLen - (lineLen / uMax) * u3
          this.iThree = lineLen - shortLineLen / iMax * i3
          this.angAdditionalOne = angAddOne
          this.angAdditionalTwo = this.angTwo + angAddTwo
          this.angAdditionalThree = this.angThree + angAddThree
          this.showLines = true
          this.phaseDataLoading = false
        })
        .catch((err) => {
          this.phaseDataLoading = false
          console.log(err)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  .small {
    font: italic 15px sans-serif;
  }
  .heavy {
    font: bold 18px sans-serif;
  }
  .phase-one {
    fill: none;
    stroke: orange;
    stroke-width: 3;
    stroke-miterlimit: 10;
  }
  .phase-two {
    fill: none;
    stroke: green;
    stroke-width: 3;
    stroke-miterlimit: 10;
  }
  .phase-three {
    fill: none;
    stroke: blue;
    stroke-width: 3;
    stroke-miterlimit: 10;
  }

  .phase-line {
    transform-origin: 210px 140px;
  }
</style>



// WEBPACK FOOTER //
// src/spa/points/PointCounterPhase.vue